import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { regions } from "@/data/clientsData";
import StagehookLogo from "@/components/icons/StagehookLogo";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdown(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(7,7,10,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid var(--line)" : "1px solid transparent",
      }}
    >
      <div className="shg-container flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <StagehookLogo size={38} radius={7} />
          <div className="leading-tight">
            <div className="font-display text-[17px] tracking-tight">STAGEHOOK</div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-[var(--text-muted)]">
              GLOBAL
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          <NavLink to="/" data-testid="nav-home" className="shg-link text-sm">
            Home
          </NavLink>

          {/* Regions dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setDropdown(true)}
            onMouseLeave={() => setDropdown(false)}
          >
            <button
              data-testid="nav-regions-trigger"
              className="shg-link text-sm flex items-center gap-1"
            >
              Regions <ChevronDown size={14} />
            </button>

            {dropdown && (
              <div
                data-testid="nav-regions-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
              >
                <div
                  className="grid grid-cols-1 gap-1 p-3 rounded-2xl min-w-[280px]"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--line-strong)",
                    boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7)",
                  }}
                >
                  {regions.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/region/${r.slug}`}
                      data-testid={`nav-region-${r.slug}`}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[var(--surface-2)] transition-colors"
                    >
                      <span className="text-2xl">{r.flag}</span>
                      <div>
                        <div className="text-sm font-medium">{r.name}</div>
                        <div className="text-[10px] text-[var(--text-muted)] font-mono tracking-[0.15em]">
                          {r.tagline}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink to="/projects" data-testid="nav-projects" className="shg-link text-sm">
            Projects
          </NavLink>
          <NavLink to="/case-studies" data-testid="nav-case-studies" className="shg-link text-sm">
            Case studies
          </NavLink>
          <NavLink to="/stand-builder" data-testid="nav-cities" className="shg-link text-sm">
            Cities
          </NavLink>
          <NavLink to="/booth" data-testid="nav-booths" className="shg-link text-sm">
            Booths
          </NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className="shg-link text-sm">
            Blog
          </NavLink>
          <NavLink to="/tools/exhibition-stand-cost-calculator" data-testid="nav-calculator" className="shg-link text-sm">
            Cost calculator
          </NavLink>
          <NavLink to="/about" data-testid="nav-about" className="shg-link text-sm">
            About
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" data-testid="nav-cta" className="btn-primary text-xs py-2.5 px-5">
            Get a Quote
          </Link>
        </div>

        {/* Mobile */}
        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden border-t border-[var(--line)]"
          style={{ background: "var(--bg-soft)" }}
        >
          <div className="shg-container py-6 flex flex-col gap-2">
            <Link to="/" className="py-3 text-base">Home</Link>
            <div className="py-2 font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)]">
              REGIONS
            </div>
            <div className="flex flex-col gap-1 mb-2">
              {regions.map((r) => (
                <Link
                  key={r.slug}
                  to={`/region/${r.slug}`}
                  className="flex items-center gap-2 py-2 text-sm"
                >
                  <span>{r.flag}</span> {r.name}
                </Link>
              ))}
            </div>
            <Link to="/projects" className="py-3 text-base">Projects</Link>
            <Link to="/case-studies" className="py-3 text-base">Case studies</Link>
            <Link to="/tools/exhibition-stand-cost-calculator" className="py-3 text-base">Cost calculator</Link>
            <Link to="/stand-builder" className="py-3 text-base">Cities</Link>
            <Link to="/booth" className="py-3 text-base">Booth designs</Link>
            <Link to="/show" className="py-3 text-base">Upcoming shows</Link>
            <Link to="/blog" className="py-3 text-base">Blog</Link>
            <Link to="/about" className="py-3 text-base">About</Link>
            <Link to="/contact" className="btn-primary mt-3 self-start">Get a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
